<template>
    <li ref="rootRef" class="issue-item" :class="{
        'issue-item--active': isActive,
        'issue-item--has-locations': hasLocations,
        'issue-item--open': hasLocations && isExpanded,
        'issue-item--rejected': isRejected,
    }" :data-issue-id="issue.id" @click="onClickCard" @mouseenter="onMouseEnterCard" @mouseleave="onMouseLeaveCard">
        <template v-if="!hasLocations">
            <div
                v-if="issue.manuscriptLevel || issue.aiPriority || displayIssueTypeLabel || issue.line > 0"
                class="issue-item__headline"
            >
                <span v-if="issue.aiPriority" class="issue-item__metadata-chip">
                    {{ t(`proofreadingReviseManuscriptPage.aiPriority.${issue.aiPriority}`) }}
                </span>
                <p v-if="headlineText" class="issue-item__headline-text">{{ headlineText }}</p>
            </div>
            <div v-if="issue.foreshadow" class="issue-item__foreshadow">
                <p class="issue-item__foreshadow-row">
                    <span>{{ t('proofreadingReviseManuscriptPage.foreshadowItem') }}</span>
                    {{ issue.foreshadow.item }}
                </p>
                <p v-if="issue.foreshadow.storyChangesHeading" class="issue-item__foreshadow-row">
                    <span>{{ t('proofreadingReviseManuscriptPage.foreshadowHeading') }}</span>
                    {{ issue.foreshadow.storyChangesHeading }}
                </p>
                <p class="issue-item__foreshadow-row">
                    <span>{{ t('proofreadingReviseManuscriptPage.foreshadowPlants') }}</span>
                    {{ issue.foreshadow.plantsSeen
                        || t('proofreadingReviseManuscriptPage.foreshadowPlantsEmpty') }}
                </p>
                <p v-if="issue.text" class="issue-item__foreshadow-row">
                    <span>{{ t('proofreadingReviseManuscriptPage.originalTextLabel') }}</span>
                    {{ issue.text }}
                </p>
            </div>
            <p v-if="displaySummary" class="issue-item__summary">{{ displaySummary }}</p>

            <div v-if="displaySuggestionText" class="issue-item__text-suggestion-wrapper">
                <div class="issue-item__text-suggestion">{{
                    t('proofreadingReviseManuscriptPage.suggestionLabel')
                }}</div>
                <p class="issue-item__text" :data-status="issue.status">{{ displaySuggestionText }}</p>
            </div>
        </template>
        <template v-else>
            <div class="issue-item__header">
                <div class="issue-item__header-main">
                    <p v-if="displayIssueTypeLabel" class="issue-item__category">{{ displayIssueTypeLabel }}</p>
                    <p class="issue-item__summary">{{ displaySummary }}</p>
                </div>
                <icon-chevron-down class="issue-item__chevron" aria-hidden="true" />
            </div>
            <div class="issue-item__locations-wrapper" :class="{ 'issue-item__locations-wrapper--open': isExpanded }"
                :aria-hidden="!isExpanded">
                <ul class="issue-item__locations">
                    <li v-for="location in issue.locations" :key="location.id" class="issue-item__location-row"
                        :class="{
                            'issue-item__location-row--active':
                                !activeLocationIds?.length || activeLocationIds.includes(location.id),
                        }"
                        :data-issue-id="location.id" @click.stop="onClickLocation(location)"
                        @mouseenter="onMouseEnterLocation(location)" @mouseleave="onMouseLeaveLocation(location)">
                        <span class="issue-item__location-row-position">{{
                            formatLocationRowPosition(location)
                        }}</span>
                        <p class="issue-item__location-original">{{
                            t('proofreadingReviseManuscriptPage.originalTextWithValue', {
                                text: location.text,
                            })
                        }}</p>
                        <div v-if="!location.applied && getLocationStatus(location) !== 'rejected'"
                            class="issue-item__location-suggestion">
                            <span class="issue-item__location-label">{{
                                t('proofreadingReviseManuscriptPage.suggestionLabel')
                            }}</span>
                            <div class="issue-item__location-actions">
                                <button v-for="candidate in getLocationCandidates()" :key="candidate" type="button"
                                    class="issue-item__candidate" @click.stop="onApplyLocation(location, candidate)">
                                    {{ candidate }}
                                </button>
                            </div>
                        </div>
                        <div v-if="location.applied" class="issue-item__location-actions">
                            <button type="button" class="issue-item__apply issue-item__apply--undo"
                                @click.stop="onRevertLocation(location)">
                                {{ revertLabel }}
                            </button>
                        </div>
                        <div v-if="!location.applied" class="issue-item__location-actions">
                            <button type="button" class="issue-item__reject"
                                :class="{ 'issue-item__reject--rejected': getLocationStatus(location) === 'rejected' }"
                                @click.stop="onRejectToggleLocation(location)">
                                {{ getLocationRejectLabel(location) }}
                            </button>
                        </div>
                    </li>
                </ul>
            </div>
        </template>
        <div v-if="!hasLocations" class="issue-item__actions">
            <button v-if="issue.fixable && !issue.applied && !isRejected" type="button" class="issue-item__apply"
                @click.stop="onApply">
                {{ applyLabel }}
            </button>
            <button v-if="issue.fixable === false && !issue.applied && !isRejected" type="button"
                class="issue-item__apply" @click.stop="onApply">
                {{ markDoneLabel }}
            </button>
            <button v-if="issue.applied" type="button" class="issue-item__apply issue-item__apply--undo"
                @click.stop="onRevert">
                {{ revertLabel }}
            </button>
            <button v-if="!issue.applied" type="button" class="issue-item__reject"
                :class="{ 'issue-item__reject--rejected': isRejected }" @click.stop="onRejectToggle">
                {{ rejectLabel }}
            </button>
        </div>
    </li>
</template>
<script lang="ts" setup>
import { computed, defineEmits, defineProps, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n-bridge';
import type {
    ProofreadingIssue,
    ProofreadingIssueLocation,
    ProofreadingIssueStatus,
} from '@/types/proofreading';
import {
    scrollToElementTop,
    SCROLL_INTO_VIEW_DURATION_MS,
} from '@/lib/utils/proofreadingEditorUtil';
import IconChevronDown from '~icons/figma/ChevronDown';

const { t } = useI18n();

const props = defineProps<{
    issue: ProofreadingIssue;
    isActive?: boolean;
    activeLocationIds?: string[];
    categoryLabel?: string;
    defaultOpen?: boolean;
}>();

const emit = defineEmits<{
    (event: 'click', payload: { issueId: string; episodeKey: string }): void;
    (event: 'apply', issue: ProofreadingIssue): void;
    (event: 'revert', issue: ProofreadingIssue): void;
    (event: 'reject-toggle', issue: ProofreadingIssue): void;
    (event: 'hover', issue: ProofreadingIssue, isHovered: boolean): void;
}>();

const applyLabel = computed(() => t('proofreadingReviseManuscriptPage.apply'));
const markDoneLabel = computed(() => t('proofreadingReviseManuscriptPage.markDone'));
const revertLabel = computed(() => t('proofreadingReviseManuscriptPage.revert'));
const isRejected = computed(() => props.issue.status === 'rejected');
const rejectLabel = computed(() =>
    t(
        isRejected.value
            ? 'proofreadingReviseManuscriptPage.rejected'
            : 'proofreadingReviseManuscriptPage.reject',
    ),
);

const hasLocations = computed(
    () => (props.issue.locations?.length ?? 0) > 0,
);

const rootRef = ref<HTMLElement | null>(null);
const isExpanded = ref(props.defaultOpen ?? false);

function scrollToAccordionTop() {
    nextTick(() => {
        if (!rootRef.value) return;
        scrollToElementTop(rootRef.value, SCROLL_INTO_VIEW_DURATION_MS);
    });
}

function expandIfActiveLocationSelected() {
    if (!hasLocations.value) return;
    const locationIds = props.activeLocationIds;
    if (locationIds == null || locationIds.length === 0) return;

    const hasActiveLocation = props.issue.locations?.some(
        (loc) => locationIds.includes(loc.id),
    );
    if (hasActiveLocation) {
        isExpanded.value = true;
    }
}

watch(
    () => props.activeLocationIds,
    () => {
        expandIfActiveLocationSelected();
    },
    { deep: true },
);

watch(
    () => props.isActive,
    (active, wasActive) => {
        if (active && wasActive === false && hasLocations.value) {
            isExpanded.value = true;
        }
    },
);

const displayIssueTypeLabel = computed(
    () => props.issue.issueTypeLabel ?? props.categoryLabel ?? '',
);

const headlineText = computed(() => {
    const typeLabel = displayIssueTypeLabel.value;
    if (props.issue.manuscriptLevel) {
        return `${typeLabel}${t('proofreadingReviseManuscriptPage.manuscriptLevelPositionParen')}`;
    }
    const location =
        props.issue.line > 0
            ? t('proofreadingReviseManuscriptPage.locationPositionParen', {
                line: props.issue.line,
                column: props.issue.column,
            })
            : '';
    return `${typeLabel}${location}`;
});

const displaySummary = computed(() => {
    const variants = props.issue.variationVariants;
    if (!variants?.length) return props.issue.summary;

    return variants
        .map((variant) =>
            t('proofreadingReviseManuscriptPage.variationVariantPart', {
                text: variant.text,
                count: variant.count,
            }),
        )
        .join(' / ');
});

const displaySuggestionText = computed(() =>
    props.issue.suggestionSegments.map((seg) => seg.text).join(''),
);

function formatLocationRowPosition(location: ProofreadingIssueLocation) {
    if (location.episodeTitle) {
        return t('proofreadingReviseManuscriptPage.location', {
            episode: location.episodeTitle,
            line: location.line,
            column: location.column,
        });
    }
    return t('proofreadingReviseManuscriptPage.locationPosition', {
        line: location.line,
        column: location.column,
    });
}

function getLocationStatus(location: ProofreadingIssueLocation): ProofreadingIssueStatus {
    return location.status ?? 'pending';
}

function getLocationCandidates(): string[] {
    return (props.issue.variationVariants ?? []).map((variant) => variant.text);
}

function getLocationRejectLabel(location: ProofreadingIssueLocation): string {
    return t(
        getLocationStatus(location) === 'rejected'
            ? 'proofreadingReviseManuscriptPage.rejected'
            : 'proofreadingReviseManuscriptPage.reject',
    );
}

function buildLocationIssuePayload(
    location: ProofreadingIssueLocation,
    replacementText?: string,
): ProofreadingIssue {
    return {
        ...props.issue,
        id: location.id,
        episodeKey: location.episodeKey,
        line: location.line,
        column: location.column,
        text: location.text,
        ...(replacementText != null ? { replacementText } : {}),
    };
}

function onClickCard() {
    if (hasLocations.value) {
        isExpanded.value = !isExpanded.value;
        if (isExpanded.value) {
            scrollToAccordionTop();
        }
    }
    emit('click', { issueId: props.issue.id, episodeKey: props.issue.episodeKey });
}

function onClickLocation(location: ProofreadingIssueLocation) {
    emit('click', { issueId: location.id, episodeKey: location.episodeKey });
}

function onApply() {
    emit('apply', props.issue);
}

function onRevert() {
    emit('revert', props.issue);
}

function onRejectToggle() {
    emit('reject-toggle', props.issue);
}

function onApplyLocation(location: ProofreadingIssueLocation, candidate: string) {
    emit('apply', buildLocationIssuePayload(location, candidate));
}

function onRevertLocation(location: ProofreadingIssueLocation) {
    emit('revert', buildLocationIssuePayload(location));
}

function onRejectToggleLocation(location: ProofreadingIssueLocation) {
    emit('reject-toggle', buildLocationIssuePayload(location));
}

function onMouseEnterCard() {
    if (hasLocations.value) return;
    emit('hover', props.issue, true);
}

function onMouseLeaveCard() {
    if (hasLocations.value) return;
    emit('hover', props.issue, false);
}

function onMouseEnterLocation(location: ProofreadingIssueLocation) {
    emit('hover', buildLocationIssuePayload(location), true);
}

function onMouseLeaveLocation(location: ProofreadingIssueLocation) {
    emit('hover', buildLocationIssuePayload(location), false);
}
</script>
<style lang="scss" scoped>
@import "@/style-common.scss";

.issue-item {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 16px;
    border: 1.5px solid var(--color-border);
    border-radius: var(--radius-sm);
    gap: 8px;
    background-color: var(--color-background-normal);
    opacity: 0.44;

    &--active {
        opacity: 1;
    }

    &--has-locations {
        gap: 12px;
    }

    &__header {
        position: sticky;
        top: 0;
        z-index: 1;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px;
        margin: -16px -16px -8px;
        padding: 16px 16px 8px;
        border-radius: var(--radius-sm) var(--radius-sm) 0 0;
        background-color: var(--color-background-normal);
    }

    &__header-main {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
        flex: 1;
    }

    &__headline {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
    }

    &__headline-text {
        margin-block: 0;
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
        line-height: var(--line-height-default);
    }

    &__category {
        margin-block: 0;
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
        line-height: var(--line-height-default);
    }

    &__chevron {
        flex-shrink: 0;
        margin-top: 2px;
        font-size: var(--font-size-md);
        color: var(--color-text-normal);
        transition: transform 0.2s ease;
        transform: rotate(0deg);
    }

    &--open &__chevron {
        transform: rotate(180deg);
    }

    &__location {
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
        margin-block: 0;
    }

    &__summary {
        font-size: var(--font-size-sm);
        color: var(--color-text-normal);
        margin-block: 0;
        font-weight: var(--font-weight-bold);
        line-height: var(--line-height-default);
    }

    &__metadata {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    }

    &__metadata-chip {
        padding: 2px 8px;
        border-radius: var(--radius-rounded);
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
        background-color: var(--color-surface-muted);
    }

    &__foreshadow {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    &__foreshadow-row {
        margin: 0;
        font-size: var(--font-size-sm);
        line-height: var(--line-height-default);
        color: var(--color-text-normal);

        span {
            display: block;
            margin-bottom: 2px;
            font-size: var(--font-size-xs);
            font-weight: var(--font-weight-bold);
            color: var(--color-text-low);
        }
    }

    &__text-suggestion {
        padding-left: 6px;
        font-size: var(--font-size-xs);
        color: var(--color-text-normal);
        font-weight: var(--font-weight-bold);
    }

    &__text {
        font-size: var(--font-size-sm);
        padding: 4px 8px;
        margin-block: 0;

        background-color: var(--color-surface-subtle);

        &[data-status="rejected"] {
            background-color: var(--color-surface-base);
        }

        &[data-status="pending"] {
            background-color: var(--color-red-3);
            color: var(--color-gray-1);
        }

        &[data-status="applied"] {
            background-color: var(--color-green-3);
            color: var(--color-gray-1);
        }
    }

    &__locations-wrapper {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 0.3s ease;

        &--open {
            grid-template-rows: 1fr;
        }
    }

    &__locations {
        overflow: hidden;
        min-height: 0;
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__location-row {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px;
        border-radius: var(--radius-sm);
        background-color: var(--color-surface-muted);
        cursor: pointer;
        opacity: 0.44;

        @include hover-overlay;

        &--active {
            opacity: 1;
        }
    }

    &__location-row-position {
        font-size: var(--font-size-xs);
        color: var(--color-text-low);
    }

    &__location-original {
        margin-block: 0;
        font-size: var(--font-size-sm);
        color: var(--color-text-normal);
        font-weight: var(--font-weight-bold);
        line-height: var(--line-height-default);
    }

    &__location-suggestion {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    &__location-label {
        font-size: var(--font-size-xs);
        color: var(--color-text-normal);
        font-weight: var(--font-weight-bold);
    }

    &__location-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    &__actions {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
    }

    &__candidate,
    &__apply,
    &__reject {
        position: relative;
        display: inline-flex;
        align-items: center;
        padding: 6px 18px;
        border: none;
        border-radius: var(--radius-rounded);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-bold);
        cursor: pointer;

        @include hover-overlay;
    }

    &__candidate {
        background-color: var(--color-surface-base);
        color: var(--color-text-normal);
        border-radius: var(--radius-sm);
        padding: 6px 12px;
        font-weight: var(--font-weight-regular);
    }

    &__apply {
        background-color: var(--color-primary);
        color: var(--color-text-on-fixed-bg);

        &--undo {
            background-color: var(--color-surface-base);
            color: var(--color-text-normal);
        }
    }

    &__reject {
        background-color: var(--color-warning);
        color: var(--color-text-on-fixed-bg);

        &--rejected {
            background-color: var(--color-surface-base);
            color: var(--color-text-normal);
        }
    }

    &:not(&--has-locations) &__reject {
        background-color: var(--color-surface-base);
        color: var(--color-text-normal);

        &--rejected {
            background-color: var(--color-surface-base);
            color: var(--color-text-normal);
        }
    }
}
</style>
